import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from "@/components/ui/select";
import { Button } from "@/components/ui/button";
import { Minus, Plus } from "lucide-react";
import { DAY_NAMES } from "@/lib/constants";
import { cn } from "@/lib/utils";

const UNIT = { daily: "day", weekly: "week", monthly: "month" };

export function FrequencyPicker({ value, onChange, testIdPrefix = "freq" }) {
  const v = { frequency: "daily", interval: 1, days_of_week: [], ...value };

  const set = (patch) => onChange({ ...v, ...patch });

  const toggleDay = (d) => {
    const has = v.days_of_week.includes(d);
    const next = has ? v.days_of_week.filter((x) => x !== d) : [...v.days_of_week, d];
    set({ days_of_week: next.sort((a, b) => a - b) });
  };

  const stepInterval = (delta) => {
    const next = Math.max(1, Math.min(60, (v.interval || 1) + delta));
    set({ interval: next });
  };

  return (
    <div className="space-y-3">
      <div className="flex items-center gap-2">
        <Select value={v.frequency} onValueChange={(f) => set({ frequency: f, interval: 1 })}>
          <SelectTrigger className="rounded-xl" data-testid={`${testIdPrefix}-frequency-select`}>
            <SelectValue />
          </SelectTrigger>
          <SelectContent>
            <SelectItem value="once">One-time</SelectItem>
            <SelectItem value="daily">Daily</SelectItem>
            <SelectItem value="weekly">Weekly</SelectItem>
            <SelectItem value="monthly">Monthly</SelectItem>
          </SelectContent>
        </Select>
      </div>

      {v.frequency !== "once" && (
        <div className="flex items-center gap-3">
          <span className="text-sm text-muted-foreground">Every</span>
          <div className="flex items-center gap-1 rounded-xl border border-border p-1">
            <Button
              type="button"
              variant="ghost"
              size="icon"
              className="h-8 w-8 rounded-lg"
              onClick={() => stepInterval(-1)}
              data-testid={`${testIdPrefix}-interval-minus`}
            >
              <Minus className="h-4 w-4" />
            </Button>
            <span className="w-8 text-center font-display font-semibold" data-testid={`${testIdPrefix}-interval-value`}>
              {v.interval || 1}
            </span>
            <Button
              type="button"
              variant="ghost"
              size="icon"
              className="h-8 w-8 rounded-lg"
              onClick={() => stepInterval(1)}
              data-testid={`${testIdPrefix}-interval-plus`}
            >
              <Plus className="h-4 w-4" />
            </Button>
          </div>
          <span className="text-sm text-muted-foreground">
            {UNIT[v.frequency]}
            {(v.interval || 1) > 1 ? "s" : ""}
          </span>
        </div>
      )}

      {v.frequency === "weekly" && (
        <div className="flex flex-wrap gap-1.5">
          {DAY_NAMES.map((name, d) => {
            const active = v.days_of_week.includes(d);
            return (
              <button
                key={name}
                type="button"
                onClick={() => toggleDay(d)}
                data-testid={`${testIdPrefix}-day-${d}`}
                className={cn(
                  "h-9 w-11 rounded-xl text-xs font-semibold transition-colors",
                  active
                    ? "bg-primary text-primary-foreground"
                    : "bg-secondary text-secondary-foreground hover:bg-secondary/70"
                )}
              >
                {name}
              </button>
            );
          })}
        </div>
      )}
    </div>
  );
}
